import _ from "lodash"
import { CircleShapeType, PolygonShapeType, ShapeType } from "../types/shape"
import {
  getCircleShapeSettingsBy,
  getScopeConfigurationFieldsBy,
  getShapeSettingsBy,
  isRequiredConvexPolygon,
} from "./configurationHelper"
import translations from "../translations"
import { ConfigurationsType } from "../types/configurations"
import { extractBBoxByCoordinates } from "./geoMapHelper"
import {
  getCoordinatesFromSelectedArea,
  isACircleShape,
  isAPolygonShape,
  isCircleSelectionVisible,
  isConcavePolygonCoordinates,
  isPolygonSelectionVisible,
} from "./shapeHelper"
import { validationType } from "./validationHelper"
import { areValidCoordinates } from "./coordinatesHelper"
import { warningToastNotification } from "./toastNotificationHelper"
import {AppParametersType} from "../types/app";
import {SearchResultsType} from "../types/product";

export const validateShape = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues: any,
  shape: ShapeType,
  language: string,
  bypassInscribedPolygon?: boolean,
  searchByProducts?: boolean,
  searchResults?: SearchResultsType
): validationType => {
  let validation: validationType = {}

  scopeFields = scopeFields || getScopeConfigurationFieldsBy(configurations)

  if (!!shape) {
    // se non posso disegnare nessuna shape, evito le altre validazioni
    if (searchByProducts && !searchResults) {
      validation["errorMessage"] = translations(
          formValues.routine ? "cannotDrawWithSearchByTileID" : "cannotDrawWithSearchByProductIdentifier",
          language
      )
    } else {
      // priority validation
      validation = validateBBoxForShape(
          configurations,
          appParameters,
          scopeFields,
          formValues,
          shape,
          language
      )

      if (_.isEmpty(validation)) {
        if (isACircleShape(shape)) {
          validation = validateCircleShape(
              configurations,
              appParameters,
              scopeFields,
              formValues,
              shape as CircleShapeType,
              language
          )
        } else if (isAPolygonShape(shape)) {
          validation = validatePolygonShape(
              configurations,
              scopeFields,
              formValues,
              shape as PolygonShapeType
          )
        }
      }
    }
  }

  if (
    !validation?.errorMessage &&
      (!!(shape as any)?.inscribedPolygon ||
          (bypassInscribedPolygon && !!shape))
  ) {
    warningToastNotification(
      translations("polygonInscribedModifiedShapeHelper", language)
    )
  }

  return validation
}

const validatePolygonShape = (
  configurations: ConfigurationsType,
  scopeFields: string,
  formValues: any,
  shape: PolygonShapeType
): validationType => {
  const validation: validationType = {}

  if (!isPolygonSelectionVisible(formValues)) {
    validation["errorMessage"] = "Polygon shape is not available"
  }

  if (
    isRequiredConvexPolygon(configurations, formValues) &&
    isConcavePolygonCoordinates(getCoordinatesFromSelectedArea(shape))
  ) {
    validation["errorMessage"] =
      "Required Convex Polygon for the selected missions"
  }

  return validation
}

const validateCircleShape = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues: any,
  circleShape: CircleShapeType,
  language: string
): validationType => {
  const validation: validationType = {}

  if (circleShape.circle_radius > 0) {
    const radius = circleShape.circle_radius / 1000
    const {
      maxRadiusTriggererName,
      maxRadius,
      minRadiusTriggererName,
      minRadius,
    } = getCircleShapeSettingsBy(
      configurations,
      appParameters,
      scopeFields,
      formValues,
      circleShape
    )

    if (maxRadius !== undefined && radius > maxRadius) {
      const triggerNameLabel = translations(maxRadiusTriggererName, language)
      validation["errorMessage"] = triggerNameLabel
        ? translations("radiusExceedsTheMaxRadiusForSelectionMessage", language)
            .replace("${triggerNameLabel}", triggerNameLabel)
            .replace("${maxRadius}", maxRadius)
            .replace("${triggerNameLabel}", triggerNameLabel)
        : translations("radiusExceedsTheMaxRadiusMessage", language)
    }

    if (minRadius !== undefined && radius < minRadius) {
      const triggerNameLabel = translations(minRadiusTriggererName, language)
      validation["errorMessage"] = triggerNameLabel
        ? translations("radiusExceedsTheMinRadiusForSelectionMessage", language)
            .replace("${triggerNameLabel}", triggerNameLabel)
            .replace("${minRadius}", minRadius)
            .replace("${triggerNameLabel}", triggerNameLabel)
        : translations("radiusExceedsTheMinRadiusMessage", language)
    }

    if (!isCircleSelectionVisible(formValues)) {
      validation["errorMessage"] = translations(
        "circularAOInotAllowedForMission",
        language
      )
    }
  } else {
    validation["errorMessage"] = translations(
      "radiusGreaterThan0message",
      language
    )
  }

  return validation
}

const validateBBoxForShape = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  scopeFields: string,
  formValues: any,
  shape: ShapeType,
  language: string
): validationType => {
  const validation: validationType = {}
  const shapeCoordinates = getCoordinatesFromSelectedArea(shape)

  if (areValidCoordinates(shapeCoordinates)) {
    const bbox = extractBBoxByCoordinates([shapeCoordinates])
    if (bbox) {
      const lat = bbox.maxLat - bbox.minLat
      const long = bbox.maxLong - bbox.minLong
      if (lat > 90 || long > 180) {
        validation["errorMessage"] = translations("areaTooBigMessage", language)
      }

      if (!(bbox.minLat < bbox.maxLat && bbox.minLong < bbox.maxLong)) {
        validation["errorMessage"] = "Points or Lines can't be drawn"
      }
    }

    if (shape.area > 0) {
      const area = shape.area / 1000000
      const { maxArea, minArea } = getShapeSettingsBy(
        configurations,
        appParameters,
        scopeFields,
        formValues,
        shape
      )

      if (maxArea !== undefined && area > maxArea) {
        validation["errorMessage"] = translations(
          "areaConstraintsMaxErrorMessage",
          language
        ).replace("${maxArea}", maxArea)
      }

      if (minArea !== undefined && area < minArea) {
        validation["errorMessage"] = translations(
          "areaConstraintsMinErrorMessage",
          language
        ).replace("${minArea}", minArea)
      }
    }
  } else {
    validation["errorMessage"] = translations(
      "incorrectCoordinatesMessage",
      language
    )
  }

  return validation
}
